/**
 * DMs dock widget — the messenger, shrunk to a floating panel.
 *
 * It is a real chat surface, not a preview: it groups consecutive messages
 * into bursts, names and colours every sender in a group chat, honours the
 * per-conversation theme picked in the full app (accent, background, bubble
 * shape, compact/bubbles layout), renders photos, GIFs, voice notes and
 * system events, publishes typing, encrypts when the conversation is
 * encrypted, and clears the unread count while you are reading.
 */

import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronRight,
  Gift,
  ImageIcon,
  ImagePlus,
  Lock,
  Maximize2,
  Mic,
  Paperclip,
  Search,
  Send,
  Smile,
  Users,
  X,
} from "lucide-react";
import { sendChatTyping, uploadChatMedia } from "@slumbr/core";
import type { DockWidget } from "@slumbr/app-sdk";
import { cn } from "@slumbr/ui";

import { usePresenceStore } from "@/stores/presence-store";
import { useTypingSubscription } from "@/realtime/use-typing-subscription";
import { getCurrentUserId, useMessengerStore } from "../stores/messenger-store";
import { getDisplayName, getConversationAvatarUrl, getOtherParticipant } from "../components/messenger-layout";
import { getBackgroundStyle } from "../components/chat-theme-settings";
import { senderColor, initialsOf } from "../components/sender-identity";
import { TwemojiText } from "../components/twemoji-text";
import { stickerOutline } from "../components/reaction-style";
import { getMessagePosition } from "../components/use-pretext";
import { useSignedImageUrl, deriveObjectKeyFromUrl } from "../components/use-signed-image-url";
import { formatClockTime, formatRelativeShort, toUnixMs } from "../utils/timestamps";
import type { CombinedMediaTab } from "../components/combined-media-picker";
import { compressImageForChat } from "../utils/compress-image";
import { generateUUID } from "@/utils/uuid";
import type { GifItem } from "../components/gif-data";

const CombinedMediaPicker = lazy(() =>
  import("../components/combined-media-picker").then((m) => ({
    default: m.CombinedMediaPicker,
  }))
);
import type { Conversation, GameInlineContent, Message, MessageContent } from "../types";
import { GameInviteCard } from "../components/game-invite-card";

const DEFAULT_ACCENT = "#e0446e";
/** How many messages the panel keeps live. Enough to scroll back a bit. */
const THREAD_WINDOW = 60;

// ─── Content helpers ────────────────────────────────────────────────────────

/** One-line preview for the conversation list and the mobile bar. */
function previewOf(content: MessageContent | undefined): string {
  if (!content) return "No messages yet";
  switch (content.type) {
    case "text":
      return content.text;
    case "image":
      return "📷 Photo";
    case "gif":
      return "GIF";
    case "voice":
      return "🎙️ Voice note";
    case "file":
      return `📎 ${content.fileName}`;
    case "game_invite":
      return `${content.icon} ${content.gameName} invite`;
    case "game_inline":
      return `${content.icon} ${content.title}`;
    case "system":
      return content.text;
    case "encrypted":
      return "🔒 Encrypted message";
    default:
      return "Message";
  }
}

function recentConversations(convs: Conversation[]): Conversation[] {
  return [...convs]
    .filter((c) => !c.isArchived)
    .sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return toUnixMs(b.updatedAt) - toUnixMs(a.updatedAt);
    });
}

/**
 * The store's per-conversation array can hold the same id twice (optimistic
 * insert + realtime echo) and is only sorted on some write paths. The full
 * conversation page does this same pass; the widget used to render the raw
 * array, which is why messages sometimes appeared twice or out of order.
 */
function normalizeMessages(list: Message[] | undefined): Message[] {
  if (!list || list.length === 0) return [];
  const byId = new Map<string, Message>();
  for (const m of list) {
    if (m.isDeleted) continue;
    const existing = byId.get(m.id);
    if (!existing || toUnixMs(m.updatedAt) >= toUnixMs(existing.updatedAt)) {
      byId.set(m.id, m);
    }
  }
  return [...byId.values()].sort(
    (a, b) => toUnixMs(a.createdAt) - toUnixMs(b.createdAt)
  );
}

// ─── Avatar ─────────────────────────────────────────────────────────────────

function Avatar({
  name,
  url,
  color,
  size = 32,
  fallbackIcon,
}: {
  name: string;
  url?: string;
  color: string;
  size?: number;
  fallbackIcon?: React.ReactNode;
}) {
  return (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/[0.07]"
      style={{ height: size, width: size, boxShadow: `inset 0 0 0 1.5px ${color}59` }}
      title={name}
    >
      {url ? (
        <img src={url} alt={name} className="h-full w-full object-cover" />
      ) : fallbackIcon ? (
        <span style={{ color }}>{fallbackIcon}</span>
      ) : (
        <span
          className="font-bold tracking-tight"
          style={{ color, fontSize: Math.round(size * 0.34) }}
        >
          {initialsOf(name)}
        </span>
      )}
    </span>
  );
}

// ─── Compact bar (mobile swipe dock) ────────────────────────────────────────

function DmsCompact() {
  const conversations = useMessengerStore((s) => s.conversations);
  const list = useMemo(() => recentConversations(conversations), [conversations]);
  const top = list[0];
  const unread = list.reduce((n, c) => n + (c.unreadCount || 0), 0);

  if (!top) return <span>No conversations yet</span>;

  // One quiet line inside the dock card — the card already says "Messages".
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {unread > 0 && (
        <span className="shrink-0 font-bold text-[#ff8fac]">{unread} new</span>
      )}
      <span className="shrink-0 font-semibold text-[#c3c9d0]">
        {getDisplayName(top)}
      </span>
      <TwemojiText
        text={previewOf(top.lastMessage?.content)}
        className="min-w-0 truncate"
      />
    </span>
  );
}

// ─── Panel: conversation list ───────────────────────────────────────────────

function DmsPanel() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const conversations = useMessengerStore((s) => s.conversations);
  const isLoading = useMessengerStore((s) => s.isLoading);
  const fetchConversations = useMessengerStore((s) => s.fetchConversations);
  const typingIndicators = useMessengerStore((s) => s.typingIndicators);
  const statuses = usePresenceStore((s) => s.statuses);

  // The dock is reachable from anywhere in the shell, including sessions that
  // never opened the messenger. Pull the list once if it's empty.
  const didFetchRef = useRef(false);
  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    if (conversations.length === 0) {
      void fetchConversations().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = selectedId
    ? conversations.find((c) => c.id === selectedId)
    : undefined;

  // A conversation can vanish while its thread is open (deleted on another
  // device). Clear the selection from an effect — doing it during render was
  // a state update in the render phase.
  useEffect(() => {
    if (selectedId && !selected) setSelectedId(null);
  }, [selectedId, selected]);

  const list = useMemo(() => {
    const all = recentConversations(conversations);
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => {
      if (getDisplayName(c).toLowerCase().includes(q)) return true;
      const last = c.lastMessage?.content;
      return last?.type === "text" && last.text.toLowerCase().includes(q);
    });
  }, [conversations, query]);

  const typingByConversation = useMemo(() => {
    const me = getCurrentUserId();
    const names = new Map<string, string[]>();
    for (const t of typingIndicators) {
      if (t.userId === me) continue;
      const list = names.get(t.conversationId) ?? [];
      list.push(t.displayName.split(" ")[0]);
      names.set(t.conversationId, list);
    }
    const map = new Map<string, string>();
    for (const [conversationId, list] of names) {
      map.set(
        conversationId,
        list.length === 1 ? `${list[0]} is typing…` : `${list.length} people are typing…`
      );
    }
    return map;
  }, [typingIndicators]);

  if (selected) {
    return <DmsThread conversation={selected} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div className="flex max-h-[calc(88dvh-4.5rem-env(safe-area-inset-bottom))] flex-col sm:max-h-[calc(72vh-3rem)]">
      <div className="shrink-0 border-b border-white/[0.05] px-2.5 py-2">
        <div className="flex items-center gap-2 rounded-full bg-white/[0.05] px-2.5 py-1.5 ring-1 ring-inset ring-white/[0.06] focus-within:ring-white/[0.14]">
          <Search className="h-3.5 w-3.5 shrink-0 text-[#71767b]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats"
            className="min-w-0 flex-1 bg-transparent text-[13px] text-[#e7e9ea] outline-none placeholder:text-[#536471]"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="text-[#71767b] transition hover:text-[#e7e9ea]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
            {isLoading && conversations.length === 0 ? (
              <>
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/10 border-t-[#d7dbdc]" />
                <p className="mt-3 text-xs text-[#71767b]">Loading chats…</p>
              </>
            ) : (
              <p className="text-xs text-[#71767b]">
                {query ? "No chats match that." : "No conversations yet."}
              </p>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-white/[0.04]">
            {list.slice(0, 30).map((c) => {
              const other =
                c.type === "direct" ? getOtherParticipant(c) : undefined;
              const presence = other
                ? statuses[other.userId]?.effectiveStatus
                : undefined;
              const typing = typingByConversation.get(c.id);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition hover:bg-white/[0.04]"
                  >
                    <span className="relative shrink-0">
                      <Avatar
                        name={getDisplayName(c)}
                        url={getConversationAvatarUrl(c)}
                        color={senderColor(other?.userId ?? c.id)}
                        size={34}
                        fallbackIcon={
                          c.type === "group" ? <Users className="h-4 w-4" /> : undefined
                        }
                      />
                      {presence && presence !== "offline" && (
                        <span
                          className={
                            "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#101118] " +
                            (presence === "online"
                              ? "bg-emerald-500"
                              : presence === "busy"
                                ? "bg-[#ff477e]"
                                : "bg-amber-400")
                          }
                        />
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-[13px] font-bold text-[#e7e9ea]">
                          {getDisplayName(c)}
                        </span>
                        {c.lastMessage && (
                          <span className="shrink-0 text-[10px] tabular-nums text-[#5f6b78]">
                            {formatRelativeShort(c.lastMessage.createdAt)}
                          </span>
                        )}
                      </span>
                      <span
                        className={
                          "mt-0.5 block truncate text-[11.5px] " +
                          (typing
                            ? "font-medium italic text-[#ff8fac]"
                            : c.unreadCount > 0
                              ? "text-[#c3c9d0]"
                              : "text-[#71767b]")
                        }
                      >
                        <TwemojiText text={typing ?? previewOf(c.lastMessage?.content)} />
                      </span>
                    </span>

                    {c.unreadCount > 0 && (
                      <span
                        className={
                          "flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-black text-white " +
                          (c.isMuted ? "bg-white/20" : "bg-[#ff477e]")
                        }
                      >
                        {c.unreadCount > 99 ? "99+" : c.unreadCount}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Panel: one thread ──────────────────────────────────────────────────────

function DmsThread({
  conversation,
  onBack,
}: {
  conversation: Conversation;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const myId = getCurrentUserId();
  const rawMessages = useMessengerStore((s) => s.messages[conversation.id]);
  const hydrateConversation = useMessengerStore((s) => s.hydrateConversation);
  const sendMessage = useMessengerStore((s) => s.sendMessage);
  const encryptAndSendMessage = useMessengerStore((s) => s.encryptAndSendMessage);
  const markAsRead = useMessengerStore((s) => s.markAsRead);
  const encryptionStates = useMessengerStore((s) => s.encryptionStates);
  const typingIndicators = useMessengerStore((s) => s.typingIndicators);
  // The dock thread renders "X is typing…" too, so it needs its own
  // per-conversation subscription (the messenger page's only covers it).
  useTypingSubscription(conversation.id);

  const [draft, setDraft] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const typingActiveRef = useRef(false);
  const typingSentAtRef = useRef(0);
  const typingStopTimerRef = useRef<number | null>(null);

  const isEncrypted =
    encryptionStates[conversation.id]?.status === "active";
  const theme = conversation.theme;
  const accent = theme?.accentColor ?? DEFAULT_ACCENT;
  const compact = theme?.layoutMode === "irc";
  const bubbleStyle = theme?.bubbleStyle ?? "rounded";
  const backgroundStyle = useMemo(() => getBackgroundStyle(theme), [theme]);

  useEffect(() => {
    void hydrateConversation(conversation.id).catch(() => {});
  }, [conversation.id, hydrateConversation]);

  const messages = useMemo(
    () => normalizeMessages(rawMessages).slice(-THREAD_WINDOW),
    [rawMessages]
  );

  // Reading in the dock counts as reading. Re-runs when new messages land so
  // the badge doesn't creep back up while the panel is open.
  useEffect(() => {
    if (conversation.unreadCount > 0) markAsRead(conversation.id);
  }, [conversation.id, conversation.unreadCount, messages.length, markAsRead]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, conversation.id]);

  const participants = useMemo(
    () =>
      new Map(
        conversation.participants.map((p) => [
          p.userId,
          { name: p.displayName, avatarUrl: p.avatarUrl },
        ])
      ),
    [conversation.participants]
  );

  const typingText = useMemo(() => {
    const names = typingIndicators
      .filter((t) => t.conversationId === conversation.id && t.userId !== myId)
      .map((t) => t.displayName.split(" ")[0]);
    if (names.length === 0) return null;
    if (names.length === 1) return `${names[0]} is typing…`;
    return `${names.length} people are typing…`;
  }, [typingIndicators, conversation.id, myId]);

  const publishTyping = useCallback(
    (isTyping: boolean) => {
      if (isTyping) {
        const now = Date.now();
        if (typingActiveRef.current && now - typingSentAtRef.current < 2000) return;
        typingSentAtRef.current = now;
      } else if (!typingActiveRef.current) {
        return;
      }
      typingActiveRef.current = isTyping;
      void sendChatTyping(conversation.id, isTyping).catch(() => {});
    },
    [conversation.id]
  );

  useEffect(() => {
    return () => {
      if (typingStopTimerRef.current) window.clearTimeout(typingStopTimerRef.current);
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        void sendChatTyping(conversation.id, false).catch(() => {});
      }
    };
  }, [conversation.id]);

  // Media tools. The rail is a single arrow by default and expands to the
  // full set on tap; typing collapses it again so the message gets the width
  // back the moment you need it. Same trick as Discord on mobile.
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const [pickerTab, setPickerTab] = useState<CombinedMediaTab | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queueLocalMessage = useMessengerStore((s) => s.queueLocalMessage);
  const updateLocalMessage = useMessengerStore((s) => s.updateLocalMessage);
  const publishLocalMessage = useMessengerStore((s) => s.publishLocalMessage);

  const sendContent = useCallback(
    (content: MessageContent) => {
      if (isEncrypted) {
        void encryptAndSendMessage(conversation.id, content);
      } else {
        sendMessage(conversation.id, content);
      }
    },
    [isEncrypted, conversation.id, encryptAndSendMessage, sendMessage]
  );

  const handleGifSelect = useCallback(
    (gif: GifItem) => {
      sendContent({
        type: "gif",
        url: gif.url,
        thumbnailUrl: gif.previewUrl,
        width: gif.width,
        height: gif.height,
        source: gif.source,
      });
      setPickerTab(null);
    },
    [sendContent]
  );

  /** Attach one image: optimistic bubble first, then compress → upload → publish. */
  const handleFile = useCallback(
    async (file: File) => {
      setUploadError(null);
      const messageId = generateUUID();
      const localPreviewUrl = URL.createObjectURL(file);
      const now = Date.now();
      const optimistic: Message = {
        id: messageId,
        conversationId: conversation.id,
        senderId: myId,
        content: { type: "image", url: localPreviewUrl, width: 800, height: 600, altText: file.name },
        status: "sending",
        reactions: [],
        isPinned: false,
        isEdited: false,
        isDeleted: false,
        createdAt: now,
        updatedAt: now,
        uploadState: "uploading",
        uploadProgress: 1,
      };
      queueLocalMessage(optimistic);

      try {
        const prepared = await compressImageForChat(file);
        const { objectKey } = await uploadChatMedia(prepared.file, conversation.id, {
          width: prepared.width,
          height: prepared.height,
          onProgress: (percent) =>
            updateLocalMessage(conversation.id, messageId, {
              uploadState: "uploading",
              uploadProgress: Math.min(95, Math.round(percent)),
            }),
        });
        let thumbnailKey: string | undefined;
        if (prepared.thumb) {
          try {
            const thumb = await uploadChatMedia(prepared.thumb.file, conversation.id, {
              width: prepared.thumb.width,
              height: prepared.thumb.height,
            });
            thumbnailKey = thumb.objectKey;
          } catch {
            // Non-fatal: the renderer falls back to the full preview.
          }
        }
        const final: Message = {
          ...optimistic,
          content: {
            type: "image",
            objectKey,
            ...(thumbnailKey ? { thumbnailKey } : {}),
            width: prepared.width,
            height: prepared.height,
            altText: file.name,
          },
          status: "sent",
          updatedAt: Date.now(),
          uploadState: undefined,
          uploadProgress: 100,
        };
        updateLocalMessage(conversation.id, messageId, final);
        publishLocalMessage(conversation.id, final);
        URL.revokeObjectURL(localPreviewUrl);
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : "Upload failed");
        updateLocalMessage(conversation.id, messageId, {
          status: "failed",
          uploadState: "failed",
        });
      }
    },
    [conversation.id, myId, queueLocalMessage, updateLocalMessage, publishLocalMessage]
  );

  const send = useCallback(() => {
    const text = draft.trim();
    if (!text) return;
    const content: MessageContent = { type: "text", text };
    // An encrypted conversation must not receive a plaintext message just
    // because it was sent from the dock.
    if (isEncrypted) {
      void encryptAndSendMessage(conversation.id, content);
    } else {
      sendMessage(conversation.id, content);
    }
    setDraft("");
    publishTyping(false);
  }, [
    draft,
    isEncrypted,
    conversation.id,
    encryptAndSendMessage,
    sendMessage,
    publishTyping,
  ]);

  const subtitle =
    typingText ??
    (conversation.type === "group"
      ? `${conversation.participants.length} members`
      : "Direct message");

  return (
    <div className="relative flex h-[calc(88dvh-4.5rem-env(safe-area-inset-bottom))] flex-col sm:h-[520px] sm:max-h-[calc(72vh-3rem)]">
      {/* Thread header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-white/[0.06] px-2 py-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to conversations"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#8b93c7] transition hover:bg-white/[0.06] hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <Avatar
          name={getDisplayName(conversation)}
          url={getConversationAvatarUrl(conversation)}
          color={accent}
          size={26}
          fallbackIcon={
            conversation.type === "group" ? <Users className="h-3.5 w-3.5" /> : undefined
          }
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1">
            <span className="truncate text-[13px] font-black text-white">
              {getDisplayName(conversation)}
            </span>
            {isEncrypted && <Lock className="h-3 w-3 shrink-0 text-emerald-400" />}
          </span>
          <span
            className={
              "block truncate text-[10.5px] " +
              (typingText ? "font-semibold text-[#ff8fac]" : "text-[#71767b]")
            }
          >
            {subtitle}
          </span>
        </span>
        <button
          type="button"
          onClick={() => navigate(`/messenger/${conversation.id}`)}
          aria-label="Open in Messenger"
          title="Open in Messenger"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#8b93c7] transition hover:bg-white/[0.06] hover:text-white"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Messages — themed exactly like the full conversation view. Relative
          so the media picker can take the whole area rather than squatting on
          half of it. */}
      <div className="relative min-h-0 flex-1">
      <div
        className="h-full overflow-y-auto px-2 py-2"
        style={{ backgroundColor: "#0b0d0f", ...backgroundStyle }}
      >
        {messages.length === 0 ? (
          <p className="py-8 text-center text-[11px] text-[#8b93c7]">
            No messages yet — say hi.
          </p>
        ) : (
          messages.map((m, i) => {
            const position = getMessagePosition(messages, i);
            const sender = participants.get(m.senderId);
            return (
              <WidgetMessage
                key={m.id}
                message={m}
                isOwn={m.senderId === myId}
                isGroup={conversation.type === "group"}
                isGrouped={position.isGrouped}
                isGroupTail={position.isGroupTail}
                senderName={sender?.name ?? "Unknown"}
                senderAvatarUrl={sender?.avatarUrl}
                accent={accent}
                compact={compact}
                bubbleStyle={bubbleStyle}
              />
            );
          })
        )}
        <div ref={endRef} />
        <div className="h-3" aria-hidden />
      </div>

      {/* Media picker fills the panel — a half-height strip in a 340px panel
          left neither the picker nor the conversation usable. */}
      {pickerTab && (
        <div className="absolute inset-0 z-20 flex flex-col bg-[#0b0d0f] [&>div]:!h-full">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/10 border-t-[#d7dbdc]" />
              </div>
            }
          >
          <CombinedMediaPicker
            activeTab={pickerTab}
            onTabChange={setPickerTab}
            onGifSelect={handleGifSelect}
            onEmojiSelect={(emoji) => setDraft((d) => d + emoji)}
            onClose={() => setPickerTab(null)}
            variant="mobile"
          />
          </Suspense>
          <button
            type="button"
            onClick={() => setPickerTab(null)}
            aria-label="Close picker"
            // Bottom-right: the picker's own tab bar owns the top edge.
            className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-[#c3c9d0] shadow-lg backdrop-blur transition hover:bg-black/80 hover:text-white"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      </div>

      <div className="shrink-0 border-t border-white/[0.06] bg-[#0e1114]/95 backdrop-blur-xl">
        {uploadError && (
          <div className="flex items-center gap-2 border-b border-red-500/20 bg-red-500/[0.10] px-3 py-1.5">
          <span className="min-w-0 flex-1 truncate text-[11px] text-red-300">{uploadError}</span>
          <button
            type="button"
            onClick={() => setUploadError(null)}
            aria-label="Dismiss"
            className="shrink-0 text-red-300/70 transition hover:text-red-200"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Composer — the bar is the surface, matching the full conversation
          view: no nested card, ghost tool buttons, one accent send. */}
      <div className="flex items-end gap-0.5 px-1.5 py-1.5">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
        />

        {/* Tool rail. Both states are always mounted so the swap is a width +
            slide transition rather than a pop. */}
        <div
          className="relative flex shrink-0 items-center overflow-hidden transition-[width] duration-200 ease-out motion-reduce:transition-none"
          style={{ width: toolsExpanded ? 134 : 32 }}
        >
          <div
            className={cn(
              "absolute left-0 flex items-center transition-all duration-200 ease-out motion-reduce:transition-none",
              toolsExpanded
                ? "pointer-events-none -translate-x-1 opacity-0"
                : "translate-x-0 opacity-100"
            )}
          >
            <WidgetTool
              label="Show attachment options"
              onClick={() => setToolsExpanded(true)}
            >
              <ChevronRight className="h-[18px] w-[18px]" />
            </WidgetTool>
          </div>

          <div
            className={cn(
              "flex items-center gap-0.5 transition-all duration-200 ease-out motion-reduce:transition-none",
              toolsExpanded
                ? "translate-x-0 opacity-100"
                : "pointer-events-none -translate-x-3 opacity-0"
            )}
          >
            <WidgetTool label="Attach image" onClick={() => fileInputRef.current?.click()}>
              <ImagePlus className="h-[17px] w-[17px]" />
            </WidgetTool>
            <WidgetTool
              label="GIFs"
              active={pickerTab === "gifs"}
              onClick={() => setPickerTab((t) => (t === "gifs" ? null : "gifs"))}
            >
              <span className="text-[9px] font-black tracking-tight">GIF</span>
            </WidgetTool>
            <WidgetTool
              label="Stickers"
              active={pickerTab === "stickers"}
              onClick={() => setPickerTab((t) => (t === "stickers" ? null : "stickers"))}
            >
              <Gift className="h-[17px] w-[17px]" />
            </WidgetTool>
            <WidgetTool
              label="Emoji"
              active={pickerTab === "emotes"}
              onClick={() => setPickerTab((t) => (t === "emotes" ? null : "emotes"))}
            >
              <Smile className="h-[17px] w-[17px]" />
            </WidgetTool>
          </div>
        </div>

        <textarea
          value={draft}
          rows={1}
          onChange={(e) => {
            setDraft(e.target.value);
            setToolsExpanded(false);
            if (typingStopTimerRef.current) {
              window.clearTimeout(typingStopTimerRef.current);
            }
            if (e.target.value.trim()) {
              publishTyping(true);
              typingStopTimerRef.current = window.setTimeout(
                () => publishTyping(false),
                1600
              );
            } else {
              publishTyping(false);
            }
          }}
          onFocus={() => setToolsExpanded(false)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || e.shiftKey) return;
            // Windows IME commits with Enter — that keystroke isn't ours.
            if (e.nativeEvent.isComposing || (e.nativeEvent as KeyboardEvent).keyCode === 229) {
              return;
            }
            e.preventDefault();
            send();
          }}
          placeholder={isEncrypted ? "Encrypted message" : "Write a message"}
          className="block max-h-24 min-h-8 min-w-0 flex-1 resize-none bg-transparent px-1.5 py-[7px] text-[13.5px] leading-[18px] text-[#e7e9ea] outline-none placeholder:text-[#5b6572]"
        />

        {draft.trim() ? (
          <button
            type="button"
            onClick={send}
            aria-label="Send"
            title="Send message"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white transition active:scale-95"
            style={{ backgroundColor: accent, boxShadow: `0 5px 14px -5px ${accent}` }}
          >
            <Send className="h-4 w-4" />
          </button>
        ) : (
          isEncrypted && (
            <span
              className="flex h-8 w-6 shrink-0 items-center justify-center text-emerald-400"
              title="End-to-end encrypted"
            >
              <Lock className="h-3.5 w-3.5" />
            </span>
          )
        )}
      </div>
      </div>
    </div>
  );
}

/** Ghost icon button in the widget composer — matches the full app's rail. */
function WidgetTool({
  label,
  onClick,
  active,
  children,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition " +
        (active
          ? "bg-white/[0.1] text-[#e7e9ea]"
          : "text-[#8b95a1] hover:bg-white/[0.07] hover:text-[#e7e9ea]")
      }
    >
      {children}
    </button>
  );
}

// ─── One message in the panel ───────────────────────────────────────────────

function WidgetMessage({
  message,
  isOwn,
  isGroup,
  isGrouped,
  isGroupTail,
  senderName,
  senderAvatarUrl,
  accent,
  compact,
  bubbleStyle,
}: {
  message: Message;
  isOwn: boolean;
  isGroup: boolean;
  isGrouped: boolean;
  isGroupTail: boolean;
  senderName: string;
  senderAvatarUrl?: string;
  accent: string;
  compact: boolean;
  bubbleStyle: "rounded" | "square" | "minimal";
}) {
  const nameColor = isOwn ? accent : senderColor(message.senderId);

  if (message.content.type === "system") {
    return (
      <div className="flex justify-center py-1">
        <span className="rounded-full bg-black/30 px-2.5 py-0.5 text-[10px] text-[#9aa4b0]">
          {message.content.text}
        </span>
      </div>
    );
  }

  const body = <MessageBody message={message} accent={accent} isOwn={isOwn} />;
  const reactions =
    message.reactions.length > 0 ? (
      <WidgetReactions message={message} accent={accent} />
    ) : null;

  // Compact layout — mirrors the "irc" mode of the full conversation view.
  if (compact) {
    return (
      <div className={"flex items-start gap-2 " + (isGrouped ? "pt-[2px]" : "pt-2.5")}>
        <span className="w-6 shrink-0">
          {!isGrouped && (
            <Avatar name={senderName} url={senderAvatarUrl} color={nameColor} size={24} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          {!isGrouped && (
            <div className="flex items-baseline gap-1.5">
              <span
                className="truncate text-[12px] font-bold leading-4"
                style={{ color: nameColor }}
              >
                {isOwn ? "You" : senderName}
              </span>
              <span className="shrink-0 text-[9.5px] tabular-nums leading-4 text-white/45">
                {formatClockTime(message.createdAt)}
              </span>
            </div>
          )}
          <div className="text-[12.5px] leading-[18px] text-[#dfe3e7]">{body}</div>
          {reactions}
        </div>
      </div>
    );
  }

  // Bubbles layout — stacked bursts, avatar on the burst's last line.
  const R = bubbleStyle === "square" ? 8 : 16;
  const r = bubbleStyle === "square" ? 8 : 5;
  const radius: React.CSSProperties =
    bubbleStyle === "minimal"
      ? {}
      : isOwn
        ? {
            borderTopLeftRadius: R,
            borderBottomLeftRadius: R,
            borderTopRightRadius: isGrouped ? r : R,
            borderBottomRightRadius: isGroupTail ? R : r,
          }
        : {
            borderTopRightRadius: R,
            borderBottomRightRadius: R,
            borderTopLeftRadius: isGrouped ? r : R,
            borderBottomLeftRadius: isGroupTail ? R : r,
          };

  const showAvatar = isGroup && !isOwn;

  return (
    <div
      className={
        "flex items-start gap-1.5 " +
        (isGrouped ? "mt-[2px] " : "mt-2.5 ") +
        (isOwn ? "justify-end" : "justify-start")
      }
    >
      {/* Avatar sits beside the whole column and top-aligned, so its top edge
          is level with the sender name — same rule as the full chat view. */}
      {showAvatar && (
        <span className="w-6 shrink-0">
          {!isGrouped && (
            <Avatar name={senderName} url={senderAvatarUrl} color={nameColor} size={24} />
          )}
        </span>
      )}
      <div className={"flex min-w-0 max-w-[80%] flex-col " + (isOwn ? "items-end" : "items-start")}>
        {showAvatar && !isGrouped && (
          <span
            className="mb-0.5 ml-1 max-w-full truncate text-[10.5px] font-bold leading-3"
            style={{ color: nameColor }}
          >
            {senderName}
          </span>
        )}
        <div
          className={
            "min-w-0 max-w-full px-2.5 py-1.5 text-[12.5px] leading-[18px] " +
            (isOwn
              ? "text-white"
              : "bg-white/[0.08] text-[#e7e9ea] ring-1 ring-inset ring-white/[0.06]")
          }
          style={{
            ...radius,
            ...(isOwn ? { backgroundColor: accent } : {}),
          }}
        >
          {body}
        </div>
        {reactions}
        {isGroupTail && (
          <span className="mt-0.5 px-1 text-[9.5px] tabular-nums text-white/50">
            {formatClockTime(message.createdAt)}
          </span>
        )}
      </div>
    </div>
  );
}

/** Reaction chips — tap to add or remove yours, same as the full app. */
function WidgetReactions({ message, accent }: { message: Message; accent: string }) {
  const addReaction = useMessengerStore((s) => s.addReaction);
  const removeReaction = useMessengerStore((s) => s.removeReaction);
  const me = getCurrentUserId();
  const total = message.reactions.reduce((sum, r) => sum + r.userIds.length, 0);

  return (
    <span className="mt-1 flex h-5 items-center gap-1.5">
      <span className="flex items-center -space-x-1 text-[15px] leading-none">
        {message.reactions.map((r) => {
          const mine = r.userIds.includes(me);
          return (
            <button
              key={r.emoji}
              type="button"
              onClick={() =>
                mine
                  ? removeReaction(message.conversationId, message.id, r.emoji)
                  : addReaction(message.conversationId, message.id, r.emoji)
              }
              title={mine ? `You reacted ${r.emoji} — click to remove` : `React ${r.emoji}`}
              className={
                "flex h-5 w-5 shrink-0 items-center justify-center transition hover:z-10 hover:scale-110 " +
                (mine ? "z-10" : "")
              }
            >
              <span
                className="block"
                style={{ filter: stickerOutline(mine ? accent : "#ffffff") }}
              >
                <TwemojiText text={r.emoji} />
              </span>
            </button>
          );
        })}
      </span>
      {total > 1 && (
        <span className="text-[10px] font-bold tabular-nums text-white/70 [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
          {total}
        </span>
      )}
    </span>
  );
}

/** Renders whatever the message actually is — not "[image]". */
function MessageBody({
  message,
  accent,
  isOwn,
}: {
  message: Message;
  accent: string;
  isOwn: boolean;
}) {
  const content = message.content;

  if (content.type === "text") {
    return (
      <TwemojiText text={content.text} className="whitespace-pre-wrap break-words" />
    );
  }
  if (content.type === "image") {
    return (
      <WidgetImage
        conversationId={message.conversationId}
        objectKey={content.thumbnailKey ?? content.objectKey}
        fallbackUrl={content.thumbnailUrl ?? content.url}
        width={content.width}
        height={content.height}
        alt={content.altText ?? "Photo"}
        uploading={message.uploadState === "uploading" || message.uploadState === "processing"}
      />
    );
  }
  if (content.type === "gif") {
    return (
      <WidgetImage
        fallbackUrl={content.thumbnailUrl ?? content.url}
        width={content.width}
        height={content.height}
        alt="GIF"
        badge="GIF"
      />
    );
  }
  if (content.type === "voice") {
    const seconds = Math.max(1, Math.round(content.durationMs / 1000));
    return (
      <span className="inline-flex items-center gap-1.5">
        <Mic className="h-3.5 w-3.5" style={{ color: isOwn ? "#fff" : accent }} />
        <span>
          Voice note · {Math.floor(seconds / 60)}:
          {String(seconds % 60).padStart(2, "0")}
        </span>
      </span>
    );
  }
  if (content.type === "file") {
    return (
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <Paperclip className="h-3.5 w-3.5 shrink-0 opacity-70" />
        <span className="truncate">{content.fileName}</span>
      </span>
    );
  }
  if (content.type === "game_invite") {
    // The card is only 128px tall, so it fits the dock panel as-is and Join
    // navigates exactly the same way it does in the full messenger.
    return <GameInviteCard content={content} compact />;
  }
  if (content.type === "game_inline") {
    // An in-chat game needs far more room than this panel has. Point at the
    // full conversation rather than cramming a board into a floating widget.
    return <InlineGamePointer content={content} conversationId={message.conversationId} />;
  }
  if (content.type === "encrypted") {
    return (
      <span className="inline-flex items-center gap-1.5 italic opacity-70">
        <Lock className="h-3 w-3" />
        Decrypting…
      </span>
    );
  }
  return <span className="italic opacity-70">Message</span>;
}

/** Compact stand-in for an inline game: opens the conversation to play it. */
function InlineGamePointer({
  content,
  conversationId,
}: {
  content: GameInlineContent;
  conversationId: string;
}) {
  const navigate = useNavigate();
  const accent = content.accent || "#8b95a1";

  return (
    <button
      type="button"
      onClick={() => navigate(`/messenger/${conversationId}`)}
      className="mt-1 flex w-full items-center gap-2 rounded-xl border px-2.5 py-2 text-left transition hover:bg-white/[0.04]"
      style={{ borderColor: `${accent}33`, backgroundColor: `${accent}12` }}
    >
      <span className="text-[15px]">{content.icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12px] font-semibold text-white/90">
          {content.title}
        </span>
        <span className="block truncate text-[10.5px] text-white/45">
          Open the chat to play
        </span>
      </span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/35" />
    </button>
  );
}

const WIDGET_IMAGE_MAX_W = 190;
const WIDGET_IMAGE_MAX_H = 150;

function WidgetImage({
  conversationId,
  objectKey,
  fallbackUrl,
  width,
  height,
  alt,
  badge,
  uploading,
}: {
  conversationId?: string;
  objectKey?: string;
  fallbackUrl?: string;
  width: number;
  height: number;
  alt: string;
  badge?: string;
  uploading?: boolean;
}) {
  const resolvedKey = objectKey ?? deriveObjectKeyFromUrl(fallbackUrl);
  const resolved = useSignedImageUrl(resolvedKey, conversationId ?? "", fallbackUrl ?? "");
  const src = resolvedKey ? resolved : fallbackUrl;
  const scale = Math.min(
    1,
    WIDGET_IMAGE_MAX_W / Math.max(1, width),
    WIDGET_IMAGE_MAX_H / Math.max(1, height)
  );

  return (
    <span
      className="relative block overflow-hidden rounded-xl bg-white/[0.06]"
      style={{ width: Math.round(width * scale), height: Math.round(height * scale) }}
    >
      {src ? (
        <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center">
          <ImageIcon className="h-5 w-5 text-[#5f6b78]" />
        </span>
      )}
      {badge && (
        <span className="absolute bottom-1 left-1 rounded bg-black/65 px-1 py-0.5 text-[9px] font-bold leading-none text-white">
          {badge}
        </span>
      )}
      {uploading && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/45">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        </span>
      )}
    </span>
  );
}

// ─── Registration ───────────────────────────────────────────────────────────

export const dmsWidget: DockWidget = {
  id: "messenger.dms",
  appId: "messenger",
  title: "Messages",
  icon: "💬",
  contextual: false,
  badge: () => {
    const total = useMessengerStore
      .getState()
      .conversations.reduce((n, c) => n + (c.unreadCount || 0), 0);
    return total > 0 ? total : undefined;
  },
  CompactUI: DmsCompact,
  PanelUI: DmsPanel,
};
